import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Tenant, TenantContact } from '../types';
import { API_URL } from '../config';
import './Tenants.css';

const Tenants: React.FC = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);

  const fetchTenants = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/api/tenants`, config);
      setTenants(response.data.tenants || []);
    } catch (error: any) {
      console.error('Failed to fetch tenants:', error);
      if (error.response?.status === 403) {
        // Access denied - will be handled by axios interceptor
        return;
      }
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleDeleteTenant = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tenants/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTenants();
    } catch (error: any) {
      console.error('Failed to delete tenant:', error);
      alert(error.response?.data?.message || 'Failed to delete tenant');
    }
  };

  if (loading) {
    return <div className="tenants-loading">Loading...</div>;
  }

  return (
    <div className="tenants-page">
      <header className="tenants-header">
        <div className="header-content">
          <h1>Building Tenants</h1>
          <p>Contact information for tenants at 18 Graf Road</p>
        </div>
        {user?.role === 'admin' && (
          <button className="add-tenant-btn" onClick={() => {
            setEditingTenant(null);
            setShowTenantModal(true);
          }}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Tenant
          </button>
        )}
      </header>

      <div className="tenants-grid">
        {tenants.map((tenant) => (
          <TenantCard
            key={tenant._id}
            tenant={tenant}
            isAdmin={user?.role === 'admin'}
            onEdit={() => {
              setEditingTenant(tenant);
              setShowTenantModal(true);
            }}
            onDelete={() => handleDeleteTenant(tenant._id)}
          />
        ))}
        {tenants.length === 0 && (
          <div className="no-tenants">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
            <p>No tenants found</p>
          </div>
        )}
      </div>

      {showTenantModal && (
        <TenantModal
          tenant={editingTenant}
          onClose={() => {
            setShowTenantModal(false);
            setEditingTenant(null);
          }}
          onSuccess={() => {
            setShowTenantModal(false);
            setEditingTenant(null);
            fetchTenants();
          }}
        />
      )}
    </div>
  );
};

// Tenant Card Component
const TenantCard: React.FC<{
  tenant: Tenant;
  isAdmin?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ tenant, isAdmin, onEdit, onDelete }) => {
  const primaryContact = tenant.contacts.find(c => c.isPrimary) || tenant.contacts[0];
  const otherContacts = tenant.contacts.filter(c => c !== primaryContact);

  const formatDate = (date?: string) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <article className="tenant-card">
      <div className="card-header">
        <div className="unit-badge">Unit {tenant.unit}</div>
        {tenant.leaseEndDate && (
          <div className="lease-badge">
            Lease ends {formatDate(tenant.leaseEndDate)}
          </div>
        )}
      </div>

      <h3 className="tenant-name">{tenant.displayName}</h3>

      {tenant.notes && (
        <p className="tenant-notes">{tenant.notes}</p>
      )}

      <div className="contacts-section">
        {primaryContact && (
          <div className="contact-item primary">
            {primaryContact.email && (
              <a href={`mailto:${primaryContact.email}`} className="contact-detail email">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                {primaryContact.email}
              </a>
            )}
            {primaryContact.phone && (
              <a href={`tel:${primaryContact.phone}`} className="contact-detail phone">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {primaryContact.phone}
              </a>
            )}
          </div>
        )}

        {otherContacts.length > 0 && (
          <div className="other-contacts">
            {otherContacts.map((contact, idx) => (
              <div key={idx} className="contact-item">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="contact-detail email">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    {contact.email}
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="contact-detail phone">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    {contact.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="card-actions">
          <button className="edit-btn" onClick={onEdit}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit
          </button>
          <button className="delete-btn" onClick={onDelete}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Delete
          </button>
        </div>
      )}
    </article>
  );
};

// Tenant Modal Component
const TenantModal: React.FC<{
  tenant: Tenant | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ tenant, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    displayName: tenant?.displayName || '',
    unit: tenant?.unit || '',
    leaseStartDate: tenant?.leaseStartDate ? tenant.leaseStartDate.split('T')[0] : '',
    leaseEndDate: tenant?.leaseEndDate ? tenant.leaseEndDate.split('T')[0] : '',
    notes: tenant?.notes || '',
  });
  
  const [contacts, setContacts] = useState<TenantContact[]>(
    tenant?.contacts && tenant.contacts.length > 0 
      ? tenant.contacts 
      : [{ name: '', email: '', phone: '', isPrimary: true }]
  );

  const addContact = () => {
    setContacts([...contacts, { name: '', email: '', phone: '', isPrimary: false }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= 1) return;
    const updated = contacts.filter((_, i) => i !== index);
    // Ensure at least one is primary
    if (!updated.some(c => c.isPrimary) && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    setContacts(updated);
  };

  const updateContact = (index: number, field: keyof TenantContact, value: string | boolean) => {
    const updated = [...contacts];
    if (field === 'isPrimary' && value === true) {
      // Only one can be primary
      updated.forEach((c, i) => {
        c.isPrimary = i === index;
      });
    } else {
      (updated[index] as any)[field] = value;
    }
    setContacts(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Admin can submit with empty fields - no validation required
    const validContacts = contacts.filter(c => c.name.trim() || c.email?.trim() || c.phone?.trim());

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        displayName: formData.displayName || '',
        unit: formData.unit || '',
        contacts: validContacts.length > 0 ? validContacts : [],
        leaseStartDate: formData.leaseStartDate || undefined,
        leaseEndDate: formData.leaseEndDate || undefined,
        notes: formData.notes || undefined,
      };

      if (tenant?._id) {
        await axios.put(`${API_URL}/api/tenants/${tenant._id}`, payload, config);
      } else {
        await axios.post(`${API_URL}/api/tenants`, payload, config);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save tenant:', error);
      alert(error.response?.data?.message || 'Failed to save tenant');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{tenant?._id ? 'Edit Tenant' : 'Add New Tenant'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="e.g., John Smith or Smith Family"
                  className={!formData.displayName.trim() ? 'empty-field' : ''}
                />
              </div>
              <div className="form-group small">
                <label>Unit</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="e.g., 2A"
                  className={!formData.unit.trim() ? 'empty-field' : ''}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Lease Start Date</label>
                <input
                  type="date"
                  value={formData.leaseStartDate}
                  onChange={(e) => setFormData({ ...formData, leaseStartDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Lease End Date</label>
                <input
                  type="date"
                  value={formData.leaseEndDate}
                  onChange={(e) => setFormData({ ...formData, leaseEndDate: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Notes</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., Additional information"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header-row">
              <h3>Contacts</h3>
              <button type="button" className="add-contact-btn" onClick={addContact}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Contact
              </button>
            </div>

            {contacts.map((contact, index) => (
              <div key={index} className="contact-form-group">
                <div className="contact-header">
                  <label className="primary-checkbox">
                    <input
                      type="radio"
                      name="primaryContact"
                      checked={contact.isPrimary}
                      onChange={() => updateContact(index, 'isPrimary', true)}
                    />
                    <span>Primary Contact</span>
                  </label>
                  {contacts.length > 1 && (
                    <button 
                      type="button" 
                      className="remove-contact-btn"
                      onClick={() => removeContact(index)}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="form-row three-col">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => updateContact(index, 'name', e.target.value)}
                      placeholder="Full name"
                      className={!contact.name.trim() ? 'empty-field' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={contact.email || ''}
                      onChange={(e) => updateContact(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                      className={!contact.email?.trim() ? 'empty-field' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={contact.phone || ''}
                      onChange={(e) => updateContact(index, 'phone', e.target.value)}
                      placeholder="(555) 555-5555"
                      className={!contact.phone?.trim() ? 'empty-field' : ''}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {tenant?._id ? 'Save Changes' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Tenants;
